import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import ace, { Ace } from 'ace-builds';
import AceEditor from 'react-ace';
import Icon from '@ant-design/icons';
import SaveFilled from '@ant-design/icons/SaveFilled';
import QuestionCircleFilled from '@ant-design/icons/QuestionCircleOutlined';

import ScriptWidgetProcessor, {
	COMPILE_COMMAND,
} from '@kemu-io/kemu-core/widgets/script/index.js';
import {
	ScriptWidgetState,
} from '@kemu-io/kemu-core/widgets/script/types.js';
import { DEFAULT_MAIN_PAGE_ID, getDefaultState } from '@kemu-io/kemu-core/widgets/script/common.js';
import { CustomWidgetState, WidgetPortContext, WidgetType } from '@kemu-io/kemu-core/types';
import Button from 'antd/lib/button';
import Tooltip from 'antd/lib/tooltip';
import message from 'antd/lib/message';
import { Data, DataType } from '@kemu-io/hs-types';
import useReactiveWidgetState from '../../../common/hooks/useReactiveWidgetState';
import {
	GateCustomSettingsProps,
	GetPortsInformationFunction,
	GateUI,
	GateUIProps
} from '../index.ts';
import { customAceEditorCompleter, setCompletionContext } from './autoComplete';
import { ReactComponent as ScriptWidgetIcon } from './icon.svg';
import styles from './script.module.css';
import ScriptCodingAssistant from './ScriptCodingAssistant/ScriptCodingAssistant.tsx';
import StandardModal from '@components/modal/standardModal';
import FormGroup from '@components/form-control/formGroup/formGroup';
import GateIcon from '@components/gateIcon/gateIcon';
import { SITE_LAYOUT_RIGHT_SECTION_CLASS, STOP_SHORTCUT_PROPAGATION_CLASS } from '@common/constants';
import { selectCurrentRecipe, selectedBlock } from '@src/features/Workspace/workspaceSlice';
import { invokeWidgetProcessor } from '@src/app/recipe/utils';
import { PortLocation } from '@src/types/canvas_t';
import useTranslation from '@hooks/useTranslation';
import 'ace-builds/src-noconflict/worker-javascript.js';
import 'ace-builds/src-noconflict/mode-javascript';
import 'ace-builds/src-noconflict/theme-monokai';
import 'ace-builds/src-noconflict/ext-language_tools';
import { ReactComponent as AiAssistedIcon } from '@src/assets/img/ai-assisted-icon.svg';
import { useLogicMapperContext } from '@common/context/LogicMapperContext/LogicMapperContext.tsx';

// import ace from 'ace-builds/src-noconflict/ace';
// FIXME: ugly workaround from https://github.com/securingsincity/react-ace/issues/725
ace.config.set('basePath', 'https://cdn.jsdelivr.net/npm/ace-builds@1.36.5/src-noconflict/');
ace.config.setModuleUrl('ace/mode/javascript_worker', 'https://cdn.jsdelivr.net/npm/ace-builds@1.36.5/src-noconflict/worker-javascript.js');

// import globals from '@common/globals.ts';


type SelectionDetails = {
	text: string;
	startRow: number;
	startColumn: number;
	endRow: number;
	endColumn: number;
} | null;

type Props = GateUIProps

import useWidgetState from '@hooks/useWidgetState.ts';
import { resetWidgetInstance } from '@src/features/LogicMapper/logicMapperSlice.ts';

// if (globals.STAGE === 'development') {
// 	setLogger({
// 		...console,
// 		log: (msg: string) => console.log(`[${new Date().toISOString()}] ${msg}`)
// 	});
// }

const DefaultWidgetHeight = 60;

/**
 * Calculates widget height based on the number of ports
 */
const calculateWidgetHeight = (inputs: number | undefined, outputs: number | undefined): number => {
	// 17 pixels per port
	const portsHeight = ((Math.max(inputs || 1, outputs || 1)) * 17);
	return portsHeight;
};

const ScriptWidget = (props: Props): React.JSX.Element => {
	const [state] = useReactiveWidgetState<ScriptWidgetState>(props.recipeId, props.thingRecipeId, props.info.id);

	const fixedState: ScriptWidgetState = {
		...getDefaultState(),
		...state,
	};

	const portContext: WidgetPortContext = useMemo(() => ({
		recipePoolId: props.recipeId,
		recipeType: props.recipeType,
		thingRecipeId: props.thingRecipeId,
		widgetId: props.info.id,
	}), [props.recipeId, props.recipeType, props.thingRecipeId, props.info.id]);

	const portsHeight = useMemo(() => {
		const stateForPorts: CustomWidgetState<ScriptWidgetState> = {
			...getDefaultState(),
			...state,
		};
		const outputNames = ScriptWidgetProcessor.getOutputNames(stateForPorts, portContext);
		const inputNames = ScriptWidgetProcessor.getInputNames(stateForPorts, portContext);
		return calculateWidgetHeight(inputNames.length, outputNames.length);
	}, [state, portContext]);

	return (
		<div 
			className={classNames(styles.GateBody, {
				[styles.WidgetEnabled]: !props.info.disabled
			})}
			style={{
				minHeight: `${DefaultWidgetHeight}px`,
				...(portsHeight > DefaultWidgetHeight ? {
					height: `${portsHeight}px`,
				} : {}),
			}}
		>
			{/* {!!fixedState.$$error && (
				<div className={styles.BugIconContainer}>
					<Tooltip title={fixedState.$$error.message}>
						<BugFilled className={styles.BugIcon}/>
					</Tooltip>
				</div>
			)} */}
			<Icon component={ScriptWidgetIcon} className={styles.Icon} />
			<div className={classNames(styles.Label, {
				[styles.InFolder]: props.info.groupId
			})}>
				{fixedState.metadata?.name || 'Script'}
			</div>
		</div>
	);
};

/** Icon to be added to the bar */
const GateBarIcon = (): React.JSX.Element => {
	return (
		<GateIcon icon={<Icon component={ScriptWidgetIcon} />}/>
	);
};


// https://github.com/ajaxorg/ace/wiki/Default-Keyboard-Shortcuts
const editorCommands = [
	{  name: 'blockoutdent', exec: 'blockoutdent', bindKey: { win: 'Ctrl-[', mac: 'Command-[' }, },
	{  name: 'blockindent', exec: 'blockindent', bindKey: { win: 'Ctrl-]', mac: 'Command-]' }, },

	{  name: 'copylinesup', exec: 'copylinesup', bindKey: { win: 'Alt-Shift-Up', mac: 'Alt-Shift-Up' }, },
	{  name: 'copylinesdown', exec: 'copylinesdown', bindKey: { win: 'Alt-Shift-Down', mac: 'Alt-Shift-Down' }, },

	{  name: 'movelinesup', exec: 'movelinesup', bindKey: { win: 'Alt-Up', mac: 'Alt-Up' }, },
	{  name: 'movelinesdown', exec: 'movelinesdown', bindKey: { win: 'Alt-Down', mac: 'Alt-Down' }, },

	{  name: 'addCursorAbove', exec: 'addCursorAbove', bindKey: { win: 'Alt-Ctrl-Up', mac: 'Alt-Command-Up' }, },
	{  name: 'addCursorBelow', exec: 'addCursorBelow', bindKey: { win: 'Alt-Ctrl-Down', mac: 'Alt-Command-Down' }, },

	{  name: 'selectMoreAfter', exec: 'selectMoreAfter', bindKey: { win: 'Ctrl-D', mac: 'Command-D' }, },
	{  name: 'findAll', exec: 'findAll', bindKey: { win: 'Shift-Ctrl-L', mac: 'Shift-Command-L' }, },

];

/**
 * Some of the state properties of the script reside in the $$private section,
 * but changes to these properties are normally ignored by the useReactiveWidgetState hook.
 */
const statesDiffer = (prevState: ScriptWidgetState, newState: ScriptWidgetState): boolean => {
	const prevCode = prevState.pages[DEFAULT_MAIN_PAGE_ID].code;
	const currCode = newState.pages[DEFAULT_MAIN_PAGE_ID].code;
	const prevError = prevState.$$error?.message;
	const currError = newState.$$error?.message;
	return prevCode !== currCode || prevError !== currError;
};


const GateCustomSettings = (props: GateCustomSettingsProps): React.JSX.Element => {
	const { redrawPortsAndConnections, onClose } = props;
	const currentRecipe = useSelector(selectCurrentRecipe);
	const currentBlock = useSelector(selectedBlock);
	const { onShowHelp } = useLogicMapperContext();
	const lastEscKeyPressedAt = useRef<number>(0);
	const dispatch = useDispatch();
	const aceEditorRef = useRef<AceEditor | null>(null);
	const [annotations, setAnnotations] = useState<(Ace.Annotation & { custom?: boolean })[]>([]);
	const { getState } = useWidgetState<ScriptWidgetState>(props.recipeId, props.blockId, props.gateInfo.id);
	const [state, setState] = useReactiveWidgetState<ScriptWidgetState>(props.recipeId, props.blockId, props.gateInfo.id, statesDiffer);
	const fixedState = useMemo(() => ({
		...getDefaultState(),
		...state
	}), [state]);

	const mainPage = fixedState.pages[DEFAULT_MAIN_PAGE_ID];
	const [code, setCode] = useState(mainPage.code);
	const codeRef = useRef(code);

	// Keep ref in sync with state
	useEffect(() => {
		codeRef.current = code;
	}, [code]);

	const [selectionDetails, setSelectionDetails] = useState<SelectionDetails>(null);
	const modalContainer = useMemo(() => document.querySelector<HTMLElement>(`.${SITE_LAYOUT_RIGHT_SECTION_CLASS}`), []);
	const [modalVisible, setModalVisible] = useState(true);
	const t = useTranslation('LogicMapper.Gates.Script.Settings');

	const [assistantDrawerVisible, setAssistantDrawerVisible] = useState(false);

	const handleSettingsClosed = useCallback(() => {
		setModalVisible(false);
		// Force a re-render of the widget to show the mapping icon in the main menu
		dispatch(resetWidgetInstance({ widgetId: props.gateInfo.id }));
		onClose();
	}, [onClose, dispatch, props.gateInfo.id]);

	const handleOpenScriptHelp = useCallback(() => {
		onShowHelp({
			widgetId: props.gateInfo.id,
			type: WidgetType.script,
			recipeId: props.recipeId,
			thingRecipeId: props.blockId,
			isNativeWidget: true,
		});
	}, [onShowHelp, props.gateInfo.id, props.recipeId, props.blockId]);

	const handleAceLoaded = useCallback((editor: Ace.Editor) => {
		if (editor.completers) {
			// Remove default completers
			if (currentBlock) {
				const exists = editor.completers.find(c => c === customAceEditorCompleter);
				setCompletionContext({
					recipeId: currentBlock?.recipePoolId,
					thingRecipeId: currentBlock?.recipeId,
					widgetId: props.gateInfo.id,
				});

				if (!exists) {
					editor.completers?.push(customAceEditorCompleter);
				}
			}
			// editor.completers = [customAceEditorCompleter];
		}
	}, [currentBlock, props.gateInfo.id]);

	const handleAceChange = (value: string) => {
		setCode(value);
	};

	const handleSelectionChange = (selection: Ace.Selection) => {
		if (selection) {
			const range = selection.getRange();
			const noSelection = range.start.row === range.end.row && range.start.column === range.end.column;
			if (noSelection) {
				setSelectionDetails(null);
				return;
			}

			const selectedText = ((selection as any).doc as Ace.Document).getTextRange(range);

			// if (selectedText) {
				setSelectionDetails({
					text: selectedText,
					startRow: range.start.row,
					startColumn: range.start.column,
					endRow: range.end.row,
					endColumn: range.end.column,
				});
		}
	};

	// causes a re-render of the widget
	const reload = useCallback(() => {
		setState((currentState) => ({
			...currentState,
		}), false);
	}, [setState]);

	const onSaveScript = useCallback(async (withCode?: string) => {
		// Issue a re-compile command to the script widget.
		// We do that via a 'custom-port'
		if (currentRecipe.poolId && currentBlock && !props.gateInfo.disabled) {
			const commandEvent: Data = {
				timestamp: Date.now(),
				type: DataType.Anything,
				value: withCode || codeRef.current
			};

			await invokeWidgetProcessor(
				props.gateInfo.id,
				currentBlock.recipeId,
				currentRecipe.poolId,
				COMPILE_COMMAND,
				commandEvent
			);

			redrawPortsAndConnections();
			reload();
			message.success(t('CodeSaved', 'Code Successfully Saved'));
		}
	}, [redrawPortsAndConnections, reload, currentRecipe, currentBlock, props.gateInfo.disabled, props.gateInfo.id, t]);

	const handleSaveShortcut = useCallback((editor: Ace.Editor) => {
		// Allow saving via shortcut even if the code is disabled
		// When this method is invoked, 'code' still refers to the 
		// code when the ace editor was first mounted. Here we need
		// save using the latest version
		onSaveScript(editor.session.getValue());
	}, [onSaveScript]);


	const getCurrentCode = useCallback(() => {
		const state = getState();
		return state.pages[DEFAULT_MAIN_PAGE_ID].code;
	}, [getState]);

	const handleUpdateCode = useCallback((code: string) => {
		setCode(code);
		onSaveScript(code);
	}, [setCode, onSaveScript]);


	const handleHighlightSelectionTag = useCallback((selectionContent: string) => {
		if (aceEditorRef.current && aceEditorRef.current.editor) {
			const editor = aceEditorRef.current.editor;
			// Expected format: L<startLine>:C<startColumn>-L<endLine>:C<endColumn>
			const match = selectionContent.match(/L(\d+):C(\d+)-L(\d+):C(\d+)/);
			if (match) {
				const [, startLine, startColumn, endLine, endColumn] = match.map(Number);

				// Ace editor lines are 0-indexed
				editor.selection.setSelectionRange({
					start: { row: startLine - 1, column: startColumn -1 }, // Adjust for 0-based indexing if file content is 1-based
					end: { row: endLine - 1, column: endColumn -1 }, // Adjust for 0-based indexing
				});
				editor.scrollToRow(startLine - 1); // Scroll to the start of the selection
				editor.focus(); // Bring focus to the editor
			}
		}
	}, []);

	// Allows closing the code editor by pressing ESC twice
	useEffect(() => {
		const handleEscKey = (evt: KeyboardEvent) => {
			if (evt.key !== 'Escape') { return; }
			const now = Date.now();
			const elapsed = now - lastEscKeyPressedAt.current;
			lastEscKeyPressedAt.current = now;
			if (elapsed < 500) {
				handleSettingsClosed();
			}
		};

		document.addEventListener('keydown', handleEscKey);
		return () => {
			document.removeEventListener('keydown', handleEscKey);
		};

	}, [handleSettingsClosed]);


	// Ugly workaround to force custom annotation to stick
	// taken from: https://github.com/securingsincity/react-ace/issues/483#issuecomment-493821013
	const nextAnnotations = [
		...annotations.filter(({ custom }) => !custom),  // annotations by worker
		...(fixedState.$$error ? [{
			text: fixedState.$$error.message,
			type: 'error',
			row: fixedState.$$error?.line || 0,
			column: fixedState.$$error?.column || 0,
			custom: true,
		}] : [])
	];

	const nextAnnotationsStr = JSON.stringify(nextAnnotations);

	useEffect(() => {
		if (aceEditorRef.current && aceEditorRef.current.editor) {
			const parsedAnnotations = JSON.parse(nextAnnotationsStr);
			aceEditorRef.current.editor.getSession().setAnnotations(parsedAnnotations);
		}
	}, [nextAnnotationsStr, fixedState.$$error, aceEditorRef]);

	// console.log('Last prompt: ', chatHistory.lastPrompt);
	return (
			// This widget does not follow the same color scheme as other widgets
			// <div className={classNames(styles.SettingsContainer)}>
			// 	{props.children}
		<>
			{/* Decommissioned on 19/Aug/2023 */}
			{/* {currentRecipe.type === RecipeType.Cloud && currentRecipe.entityInfo && (
				<DependenciesModal
					visible={dependenciesModalVisible}
					recipeDbId={currentRecipe.entityInfo.dbId}
					onClose={hideDependenciesModal}
					error={installDependenciesCommand?.error}
				/>
			)} */}

			<StandardModal
				destroyOnClose={true}
				dataKemuMeta="image-warp-custom-settings"
				onCancel={handleSettingsClosed}
				closeOnMaskClick={false}
				centered={false}
				bodyClassName={styles.SettingsModalBody}
				contentWrapperClassName={styles.ModalContent}
				modalWrapperClassName={styles.ScriptWrapper}
				rootClassName={styles.ModalRoot}
				bodyStyle={{
					padding: '0px',
					height: '100%',
				}}
				className={classNames(styles.Modal/* , { [styles.ConsoleVisible]: fixedState.consoleVisible } */)}
				visible={modalVisible}
				getContainer={() => modalContainer}
				noFooter
			>
				<div
					className={classNames(styles.EditorContainer, STOP_SHORTCUT_PROPAGATION_CLASS)}
					data-testid='editor-container'
					tabIndex={0}
					onContextMenu={(e) => {
						// Stop propagation to prevent the canvas handler from intercepting the event
						// This allows the browser's default context menu to show
						e.stopPropagation();
					}}
				>
					<div className={styles.EditorBar} data-testid='editor-bar'>
						<FormGroup row={true}>
							<Tooltip placement="top" title={t('SaveBtn', 'Save script')}>
								<Button
									disabled={code === mainPage.code/*  || fixedState.pauseExecution */}
									onClick={() => onSaveScript()}
									className={classNames(styles.Button)}
									type="text"
									icon={<SaveFilled />}
									size={'large'}
								/>
							</Tooltip>

							<Tooltip placement="top" title={t('CodingAssistantBtn')}>
								<Button
									onClick={() => setAssistantDrawerVisible(true)}
									className={classNames(styles.Button)}
									type="text"
									icon={<Icon component={AiAssistedIcon} />}
									size={'large'}
								/>
							</Tooltip>

							<Tooltip placement="top" title={t('HelpBtn')}>
								<Button
									onClick={handleOpenScriptHelp}
									className={classNames(styles.Button)}
									type="text"
									icon={<QuestionCircleFilled />}
									size={'large'}
								/>
							</Tooltip>
						</FormGroup>
					</div>
					<AceEditor
						// annotations={fixedState.$$error ? [{
						// 	text: fixedState.$$error.message,
						// 	type: 'error',
						// 	row: fixedState.$$error?.line || 0,
						// 	column: fixedState.$$error?.column || 0
						// }] : []}
						className={styles.Editor}
						width=""
						height=""
						placeholder={t('Placeholder')}
						mode="javascript"
						theme="monokai"
						name={props.gateInfo.id}
						onValidate={setAnnotations}
						onLoad={handleAceLoaded}
						onChange={handleAceChange}
						onSelectionChange={handleSelectionChange}
						fontSize={12}
						showPrintMargin={true}
						showGutter={true}
						highlightActiveLine={true}
						value={code}
						commands={[
							...editorCommands,
							{
								name: 'save',
								exec: handleSaveShortcut,
								bindKey: { win: 'Ctrl-S', mac: 'Cmd-S' },
							},
						]}
						ref={aceEditorRef}
						enableBasicAutocompletion={true}
						enableLiveAutocompletion={true}
						// enableSnippets={true}

						setOptions={{
							// enableBasicAutocompletion: [customAceEditorCompleter],
							enableBasicAutocompletion: true,
							enableLiveAutocompletion: true,
							// useWorker: false,
							enableSnippets: true,
							showLineNumbers: true,
							tabSize: 2,
						}}
					/>

					<ScriptCodingAssistant
						assistantDrawerVisible={assistantDrawerVisible}
						setAssistantDrawerVisible={setAssistantDrawerVisible}
						getCurrentCode={getCurrentCode}
						onUpdateCode={handleUpdateCode}
						onHighlightSelection={handleHighlightSelectionTag}
						selectedCode={selectionDetails ? {
							startLine: selectionDetails.startRow + 1,
							startColumn: selectionDetails.startColumn + 1,
							endLine: selectionDetails.endRow + 1,
							endColumn: selectionDetails.endColumn + 1,
						} : undefined}
					/>

				</div>
			</StandardModal>
		</>
	);
};

const getPortsInformation: GetPortsInformationFunction = (state: CustomWidgetState<ScriptWidgetState>, widgetInfo) => {
	const portContext: WidgetPortContext = {
		recipePoolId: widgetInfo.recipePoolId,
		recipeType: widgetInfo.recipeType,
		thingRecipeId: widgetInfo.thingRecipeId,
		widgetId: widgetInfo.id,
	};
	const outputNames = ScriptWidgetProcessor.getOutputNames(state, portContext);
	const inputNames = ScriptWidgetProcessor.getInputNames(state, portContext);

	// Calculate input positions - spread evenly on the left
	const inputPositions: Record<string, PortLocation> = {};
	const totalInputs = inputNames.length || 1;
	inputNames.forEach((input, i) => {
		inputPositions[input.name] = [0, (i + 1) / (totalInputs + 1), -1, 0];
	});

	// Calculate output positions - spread evenly on the right
	const outputPositions: Record<string, PortLocation> = {};
	const totalOutputs = outputNames.length || 1;
	outputNames.forEach((output, i) => {
		outputPositions[output.name] = [1, (i + 1) / (totalOutputs + 1), 1, 0];
	});

	return {
		outputs: outputNames.map((output) => {
			return  {
				name: output.name,
				type: output.type,
				position: outputPositions[output.name] || [1, 0.5, 1, 0],
				...(output.jsonShape ? { jsonShape: output.jsonShape } : undefined),
				...(output.label ? { label: output.label } : undefined),
			};
		}),

		inputs: inputNames.map((input) => {
			return  {
				name: input.name,
				type: input.type,
				position: inputPositions[input.name] || [0, 0.5, -1, 0],
				...(input.jsonShape ? { jsonShape: input.jsonShape } : undefined),
				...(input.label ? { label: input.label } : undefined),
			};
		})
	};
};

export default {
	getPortsInformation,
	getWrapperClass: () => styles.CanvasIconWrapper,
	BarIcon: GateBarIcon,
	Element: ScriptWidget,
	CustomSettingsDialog: GateCustomSettings,
	hasTitle: true,
	getWidgetTitle: (intl) => intl.formatMessage({ id: 'LogicMapper.Gates.Script.Title', defaultMessage: 'Script' }),
	// Add new translation keys here if necessary for tooltips/titles related to the assistant
} as GateUI;
